// src/api.js

import { supabase } from './supabaseClient'

// ─── Gerar chave de acesso única ──────────────────────────────────────────────
export function gerarChaveAcesso() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let parte = ''
  for (let i = 0; i < 4; i++) {
    parte += chars[Math.floor(Math.random() * chars.length)]
  }
  const ano = new Date().getFullYear()
  return `LAB-${parte}-${ano}`
}

// ─── Buscar executantes do banco (e-mails e nomes) ────────────────────────────
async function getExecutantes() {
  const { data } = await supabase
    .from('usuarios_sistema')
    .select('nome, email_notificacao')
    .eq('papel', 'executante')
  if (!data) return { emails: [], nomes: [] }
  const comEmail = data.filter(u => u.email_notificacao)
  return {
    emails: comEmail.map(u => u.email_notificacao),
    nomes:  comEmail.map(u => u.nome),
  }
}

// ─── Buscar e-mail do gestor do banco ─────────────────────────────────────────
async function getEmailGestor() {
  const { data } = await supabase
    .from('usuarios_sistema')
    .select('email_notificacao')
    .eq('papel', 'gestor')
    .limit(1)
    .single()
  return data?.email_notificacao || null
}

// ─── Notificações por e-mail ──────────────────────────────────────────────────
async function notificar(tipo, os) {
  try {
    const payload = {
      tipo, os: {
        id: os.id,
        servico: os.servico,
        descricao: os.descricao,
        prazo: os.prazo,
        prioridade: os.prioridade,
        chave_acesso: os.chave_acesso || '',
        solicitante_nome: os.solicitante?.nome || os.solicitante_nome || '',
        solicitante_setor: os.solicitante?.setor || os.solicitante_setor || '',
      }
    }

    // Para notificações do gestor, busca e-mail do banco
    if (tipo === 'nova_os' || tipo === 'os_corrigida') {
      const emailGestor = await getEmailGestor()
      if (emailGestor) payload.email_gestor_override = emailGestor
    }

    // Para notificações dos executantes, busca do banco
    if (tipo === 'os_aprovada') {
      const { emails, nomes } = await getExecutantes()
      if (emails.length === 0) {
        console.warn('[notificar] Nenhum executante com e-mail cadastrado')
        return
      }
      payload.executantes_emails = emails
      payload.executantes_nomes  = nomes
    }

    const { error } = await supabase.functions.invoke('notificar-os', { body: payload })
    if (error) console.warn('[notificar] erro:', error.message)
  } catch (e) {
    console.warn('[notificar] falha:', e.message)
  }
}

// ─── Gerar ID sequencial sem duplicatas ──────────────────────────────────────
export async function gerarNovoId() {
  const ano = new Date().getFullYear()

  // Busca o maior número já usado neste ano
  const { data } = await supabase
    .from('ordens_servico')
    .select('id')
    .ilike('id', `OS-${ano}-%`)
    .order('id', { ascending: false })
    .limit(1)

  let proximoNum = 1
  if (data && data.length > 0) {
    const ultimoId = data[0].id // ex: OS-2026-007
    const ultimoNum = parseInt(ultimoId.split('-')[2]) || 0
    proximoNum = ultimoNum + 1
  }

  const num = String(proximoNum).padStart(3, '0')
  return `OS-${ano}-${num}`
}

// ─── Buscar todas as OS ───────────────────────────────────────────────────────
export async function fetchTodasOS() {
  const { data: oss, error } = await supabase
    .from('ordens_servico')
    .select(`*, solicitantes (*), historico_os (*, criado_em), anexos_os (*)`)
    .order('data_criacao', { ascending: false })
  if (error) throw error
  return oss.map(normalizar)
}

// ─── Buscar OS pela chave de acesso ──────────────────────────────────────────
export async function fetchOSporChave(chave) {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('*, solicitantes(*), historico_os(*), anexos_os(*)')
    .eq('chave_acesso', chave.trim().toUpperCase())
    .single()
  if (error) return null
  return normalizar(data)
}

// ─── Buscar OS por e-mail ou telefone (todas do solicitante) ──────────────────
export async function fetchOSporSolicitante(busca) {
  const termo = busca.trim().toLowerCase()

  // Busca na tabela solicitantes por email ou telefone
  const { data: sols } = await supabase
    .from('solicitantes')
    .select('os_id')
    .or(`email.ilike.%${termo}%,telefone.ilike.%${termo}%`)

  if (!sols || sols.length === 0) return []

  const ids = sols.map(s => s.os_id)

  const { data, error } = await supabase
    .from('ordens_servico')
    .select('*, solicitantes(*), historico_os(*), anexos_os(*)')
    .in('id', ids)
    .order('data_criacao', { ascending: false })

  if (error) return []
  return data.map(normalizar)
}

// ─── Criar nova OS → gera chave + notifica gestor ────────────────────────────
export async function criarOS(form) {
  const id = await gerarNovoId()
  const chave = gerarChaveAcesso()

  const { error: e1 } = await supabase.from('ordens_servico').insert({
    id, status: 'pendente', prioridade: form.prioridade,
    servico: form.servico, descricao: form.descricao, prazo: form.prazo,
    chave_acesso: chave,
    quantidade: form.quantidade || 1,
    justificativa_urgente: form.justificativa_urgente || null,
  })
  if (e1) throw e1

  const { error: e2 } = await supabase.from('solicitantes').insert({
    os_id: id, nome: form.nome, email: form.email,
    setor: form.setor, telefone: form.telefone,
  })
  if (e2) throw e2

  await supabase.from('historico_os').insert({
    os_id: id, acao: 'OS criada pelo solicitante', usuario: form.nome,
  })

  if (form.arquivos && form.arquivos.length > 0) await uploadAnexos(id, form.arquivos)

  await notificar('nova_os', {
    id, servico: form.servico, descricao: form.descricao,
    prazo: form.prazo, prioridade: form.prioridade, chave_acesso: chave,
    solicitante_nome: form.nome, solicitante_setor: form.setor,
  })

  // 📧 Notifica o solicitante com a chave de acesso
  if (form.email) {
    await notificar('os_criada_solicitante', {
      id, servico: form.servico, descricao: form.descricao,
      prazo: form.prazo, prioridade: form.prioridade, chave_acesso: chave,
      solicitante_email: form.email, solicitante_nome: form.nome,
    })
  }

  return { id, chave }
}

// ─── Reenviar OS após correção ────────────────────────────────────────────────
export async function reenviarOS(osId, form) {
  const { error } = await supabase.from('ordens_servico').update({
    status: 'pendente', prioridade: form.prioridade, servico: form.servico,
    descricao: form.descricao, prazo: form.prazo, obs_coord: null,
    updated_at: new Date().toISOString(),
  }).eq('id', osId)
  if (error) throw error

  await supabase.from('solicitantes').update({
    nome: form.nome, email: form.email, setor: form.setor, telefone: form.telefone,
  }).eq('os_id', osId)

  await supabase.from('historico_os').insert({
    os_id: osId, acao: 'OS reenviada após correção', usuario: form.nome,
  })

  if (form.arquivos && form.arquivos.length > 0) await uploadAnexos(osId, form.arquivos)

  await notificar('os_corrigida', {
    id: osId, servico: form.servico, descricao: form.descricao,
    prazo: form.prazo, prioridade: form.prioridade,
    solicitante_nome: form.nome, solicitante_setor: form.setor,
  })
}

// ─── Editar OS ────────────────────────────────────────────────────────────────
export async function editarOS(osId, form, gestor) {
  const { error } = await supabase.from('ordens_servico').update({
    prioridade: form.prioridade, servico: form.servico,
    descricao: form.descricao, prazo: form.prazo,
    updated_at: new Date().toISOString(),
  }).eq('id', osId)
  if (error) throw error

  await supabase.from('solicitantes').update({
    nome: form.nome, email: form.email, setor: form.setor, telefone: form.telefone,
  }).eq('os_id', osId)

  await supabase.from('historico_os').insert({
    os_id: osId, acao: 'OS editada pelo gestor', usuario: gestor,
  })

  if (form.arquivos && form.arquivos.length > 0) await uploadAnexos(osId, form.arquivos)
}

// ─── Deletar OS ───────────────────────────────────────────────────────────────
export async function deletarOS(osId) {
  const { data: anexos } = await supabase.from('anexos_os').select('storage_path').eq('os_id', osId)
  if (anexos && anexos.length > 0) {
    await supabase.storage.from('anexos-os').remove(anexos.map(a => a.storage_path))
  }
  const { error } = await supabase.from('ordens_servico').delete().eq('id', osId)
  if (error) throw error
}

// ─── Aceitar OS → notifica executantes ───────────────────────────────────────
export async function aceitarOS(osId, gestor = 'Gestor') {
  const { data } = await supabase
    .from('ordens_servico').select('*, solicitantes(*)').eq('id', osId).single()

  await supabase.from('ordens_servico').update({
    status: 'aguardando', coordenador: gestor, updated_at: new Date().toISOString(),
  }).eq('id', osId)

  await supabase.from('historico_os').insert({
    os_id: osId, acao: 'OS aprovada — adicionada à fila', usuario: gestor,
  })

  if (data) {
    const sol = data.solicitantes?.[0] || {}
    await notificar('os_aprovada', {
      id: osId, servico: data.servico, descricao: data.descricao,
      prazo: data.prazo, prioridade: data.prioridade,
      solicitante_nome: sol.nome, solicitante_setor: sol.setor,
    })
  }
}

// ─── Devolver para correção → notifica solicitante ───────────────────────────
export async function devolverOS(osId, motivo, gestor = 'Gestor') {
  const { data } = await supabase
    .from('ordens_servico').select('*, solicitantes(*)').eq('id', osId).single()

  await supabase.from('ordens_servico').update({
    status: 'correcao', coordenador: gestor, obs_coord: motivo,
    updated_at: new Date().toISOString(),
  }).eq('id', osId)
  await supabase.from('historico_os').insert({
    os_id: osId, acao: 'Devolvida para correção', usuario: gestor, obs: motivo,
  })

  // 📧 Notifica o solicitante
  if (data) {
    const sol = data.solicitantes?.[0] || {}
    if (sol.email) {
      await notificar('os_devolvida_solicitante', {
        id: osId, servico: data.servico, descricao: data.descricao,
        prazo: data.prazo, prioridade: data.prioridade,
        motivo_devolucao: motivo, gestor,
        solicitante_email: sol.email, solicitante_nome: sol.nome,
      })
    }
  }
}

// ─── Iniciar execução ─────────────────────────────────────────────────────────
export async function iniciarOS(osId, executante) {
  await supabase.from('ordens_servico').update({
    status: 'execucao', executante, updated_at: new Date().toISOString(),
  }).eq('id', osId)
  await supabase.from('historico_os').insert({
    os_id: osId, acao: `Execução iniciada por ${executante}`, usuario: executante,
  })
}

// ─── Paralisar OS → notifica solicitante ─────────────────────────────────────
export async function paralisarOS(osId, motivo, executante) {
  const { data } = await supabase
    .from('ordens_servico').select('*, solicitantes(*)').eq('id', osId).single()

  await supabase.from('ordens_servico').update({
    status: 'paralisado', updated_at: new Date().toISOString(),
  }).eq('id', osId)
  await supabase.from('historico_os').insert({
    os_id: osId, acao: 'Execução paralisada', usuario: executante, obs: motivo,
  })

  // 📧 Notifica o solicitante
  if (data) {
    const sol = data.solicitantes?.[0] || {}
    if (sol.email) {
      await notificar('os_paralisada_solicitante', {
        id: osId, servico: data.servico, descricao: data.descricao,
        prazo: data.prazo, prioridade: data.prioridade,
        motivo_paralisacao: motivo, executante,
        solicitante_email: sol.email, solicitante_nome: sol.nome,
      })
    }
  }
}

// ─── Finalizar OS → notifica solicitante ─────────────────────────────────────
export async function finalizarOS(osId, executante) {
  const { data } = await supabase
    .from('ordens_servico').select('*, solicitantes(*)').eq('id', osId).single()

  await supabase.from('ordens_servico').update({
    status: 'finalizado', updated_at: new Date().toISOString(),
  }).eq('id', osId)
  await supabase.from('historico_os').insert({
    os_id: osId, acao: 'OS finalizada com sucesso', usuario: executante,
  })

  if (data) {
    const sol = data.solicitantes?.[0] || {}
    if (sol.email) {
      await notificar('os_finalizada', {
        id: osId, servico: data.servico, descricao: data.descricao,
        prazo: data.prazo, prioridade: data.prioridade, executante,
        solicitante_email: sol.email, solicitante_nome: sol.nome,
      })
    }
  }
}

// ─── Buscar usuários (sem expor senhas) ──────────────────────────────────────
export async function fetchUsuarios() {
  const { data, error } = await supabase
    .from('usuarios_sistema')
    .select('id, nome, papel, email_notificacao, criado_em')
    .order('papel', { ascending: true })
  if (error) return []
  return data
}

// ─── Verificar login com hash seguro (via função do banco) ────────────────────
export async function verificarLogin(senha, papel) {
  const { data, error } = await supabase
    .rpc('verificar_senha', {
      email_notificacao_param: '',
      senha_param: senha,
      papel_param: papel,
    })
  if (error || !data || data.length === 0) return null
  return data[0]
}

// ─── Salvar usuário (hash da senha no banco via pgcrypto) ─────────────────────
export async function salvarUsuario(usuario) {
  if (usuario.id) {
    // Se senha foi alterada, atualiza com hash
    const updateData = {
      nome: usuario.nome,
      email_notificacao: usuario.email_notificacao,
    }
    if (usuario.senha && usuario.senha.trim()) {
      // Usa SQL direto para fazer o hash
      const { error } = await supabase.rpc('atualizar_senha_usuario', {
        usuario_id: usuario.id,
        nova_senha: usuario.senha,
        novo_nome: usuario.nome,
        novo_email: usuario.email_notificacao,
      })
      if (error) throw error
    } else {
      const { error } = await supabase
        .from('usuarios_sistema')
        .update(updateData)
        .eq('id', usuario.id)
      if (error) throw error
    }
  } else {
    // Novo usuário — insere com hash via RPC
    const { error } = await supabase.rpc('criar_usuario', {
      p_nome: usuario.nome,
      p_senha: usuario.senha,
      p_papel: usuario.papel,
      p_email: usuario.email_notificacao || '',
    })
    if (error) throw error
  }
}

export async function deletarUsuario(id) {
  const { error } = await supabase.from('usuarios_sistema').delete().eq('id', id)
  if (error) throw error
}

// ─── Upload / Download ────────────────────────────────────────────────────────
export async function uploadAnexos(osId, arquivos) {
  for (const arquivo of arquivos) {
    const nomeSeguro = arquivo.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '')
    const uid = Math.random().toString(36).substring(2, 10)
    const path = `${osId}/${uid}_${nomeSeguro}`
    const { error } = await supabase.storage.from('anexos-os').upload(path, arquivo, {
      upsert: false,
      cacheControl: '3600',
    })
    if (!error) {
      await supabase.from('anexos_os').insert({
        os_id: osId, nome: arquivo.name, storage_path: path, tamanho: arquivo.size,
      })
    } else {
      console.warn('[uploadAnexos] erro:', error.message, '| path:', path)
    }
  }
}

export async function downloadAnexo(storagePath, nomeArquivo) {
  const { data, error } = await supabase.storage.from('anexos-os').download(storagePath)
  if (error) {
    // Fallback: tenta URL pública
    const { data: urlData } = supabase.storage.from('anexos-os').getPublicUrl(storagePath)
    if (urlData?.publicUrl) {
      window.open(urlData.publicUrl, '_blank')
      return
    }
    throw error
  }
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = nomeArquivo
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Feriados nacionais brasileiros ──────────────────────────────────────────
function getFeriados(ano) {
  // Calcula Páscoa (algoritmo de Meeus/Jones/Butcher)
  const a = ano % 19, b = Math.floor(ano / 100), c = ano % 100
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mes = Math.floor((h + l - 7 * m + 114) / 31)
  const dia = ((h + l - 7 * m + 114) % 31) + 1
  const pascoa = new Date(ano, mes - 1, dia)

  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

  const feriados = [
    new Date(ano, 0, 1),   // Ano Novo
    addDays(pascoa, -48),  // Carnaval (segunda)
    addDays(pascoa, -47),  // Carnaval (terça)
    addDays(pascoa, -2),   // Sexta-feira Santa
    pascoa,                // Páscoa
    addDays(pascoa, 60),   // Corpus Christi
    new Date(ano, 3, 21),  // Tiradentes
    new Date(ano, 4, 1),   // Dia do Trabalho
    new Date(ano, 8, 7),   // Independência
    new Date(ano, 9, 12),  // Nossa Senhora Aparecida
    new Date(ano, 10, 2),  // Finados
    new Date(ano, 10, 15), // Proclamação da República
    new Date(ano, 10, 20), // Consciência Negra
    new Date(ano, 11, 25), // Natal
  ]

  return feriados.map(d => d.toISOString().split('T')[0])
}

function isFeriadoOuFimDeSemana(data) {
  const diaSemana = data.getDay()
  if (diaSemana === 0 || diaSemana === 6) return true
  const ano = data.getFullYear()
  const dataStr = data.toISOString().split('T')[0]
  return getFeriados(ano).includes(dataStr)
}

function adicionarDiasUteis(dataInicio, dias) {
  let data = new Date(dataInicio)
  let contador = 0
  while (contador < dias) {
    data.setDate(data.getDate() + 1)
    if (!isFeriadoOuFimDeSemana(data)) contador++
  }
  return data
}

// ─── Buscar configurações (férias) ───────────────────────────────────────────
export async function fetchConfiguracoes() {
  const { data } = await supabase.from('configuracoes').select('*')
  if (!data) return {}
  return Object.fromEntries(data.map(r => [r.chave, r.valor]))
}

export async function salvarConfiguracao(chave, valor) {
  const { error } = await supabase.from('configuracoes')
    .upsert({ chave, valor }, { onConflict: 'chave' })
  if (error) throw error
}

// ─── Calcular prazo estimado ──────────────────────────────────────────────────
export async function calcularPrazoEstimado(servico) {
  // Busca configurações de férias
  const config = await fetchConfiguracoes()
  const hoje = new Date()

  // Verifica se está em período de férias
  let diasMinimos = 30
  if (config.ferias_inicio && config.ferias_fim) {
    const inicio = new Date(config.ferias_inicio)
    const fim = new Date(config.ferias_fim)
    if (hoje >= inicio && hoje <= fim) {
      diasMinimos = 60
    }
  }

  // Conta OS do mesmo serviço na fila
  const { count } = await supabase
    .from('ordens_servico')
    .select('*', { count: 'exact', head: true })
    .eq('servico', servico)
    .in('status', ['pendente', 'aguardando', 'execucao'])

  const osNaFila = count || 0
  const diasTotais = diasMinimos + (osNaFila * 2)
  const dataPrazo = adicionarDiasUteis(hoje, diasTotais)

  return {
    data: dataPrazo.toLocaleDateString('pt-BR'),
    dataISO: dataPrazo.toISOString().split('T')[0],
    diasMinimos,
    osNaFila,
    diasTotais,
  }
}
function normalizar(os) {
  const sol = os.solicitantes?.[0] || {}
  return {
    id: os.id, status: os.status, prioridade: os.prioridade,
    servico: os.servico, descricao: os.descricao, prazo: os.prazo,
    quantidade: os.quantidade || 1,
    executante: os.executante, coordenador: os.coordenador,
    observacaoCoord: os.obs_coord || '',
    justificativaUrgente: os.justificativa_urgente || '',
    chaveAcesso: os.chave_acesso || '',
    dataCriacao: os.data_criacao?.split('T')[0],
    solicitante: { nome: sol.nome || '', email: sol.email || '', setor: sol.setor || '', telefone: sol.telefone || '' },
    historico: (os.historico_os || [])
      .sort((a, b) => new Date(a.criado_em) - new Date(b.criado_em))
      .map(h => ({ data: new Date(h.criado_em).toLocaleString('pt-BR'), acao: h.acao, usuario: h.usuario, obs: h.obs || '' })),
    anexos: (os.anexos_os || []).map(a => ({ nome: a.nome, path: a.storage_path, tamanho: a.tamanho })),
  }
}
